import { useEffect, useState } from "react";
import { Container } from '@mui/material';
import { NavLink, useParams } from "react-router-dom";

import { ICustomer, ICustomerOperations } from "../../../types/customer";

import { useUserContext } from "../../../contexts/userContext";

import { AppSettingService } from "../../../services/appSettingService";
import { CustomerService } from '../../../services/customerService';
import { useListContext } from "../../../contexts/listContext";

import PermissionGate from "../../permissionGate";
import Navigation from "../../header";
import CustomerDetailsForm from "../../forms/customerDetailsForm";
import CustomerAddressesForm from "../../forms/customerAddressesForm";
import CustomerContactForm from "../../forms/customerContactForm";
import CustomerCategoriesForm from "../../forms/customerCategoriesForm";
import CustomerLocationsForm from "../../forms/customerLocationsForm";
import CustomerOperationsForm from "../../forms/customerOpertationsForm";
import Spinner from "../../spinner";
import DocumentViewer from "../../documentViewer";
import DocumentTable from "../../tables/documentTable";
import { Icons } from "../../icons";

import "../../../styles/customerEdit.scss";

const CustomerEdit = () => {
	const userContext = useUserContext();
	const listContext = useListContext();
	let { id } = useParams();
	const [customer, setCustomer] = useState<ICustomer>();
	const [activeTab, setActiveTab] = useState("details");
	const [isLoadingCustomer, setIsLoadingCustomer] = useState(true);
	const [selectedDocumentId, setSelectedDocumentId] = useState<string>();
	const [entityTypeId, setEntityTypeId] = useState("");
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [brandswithApiKeys, setBrandsWithApiKeys] = useState<string[] | undefined>();

	useEffect(() => {
		getCustomer();
		loadAppSettings();
	}, [])

	useEffect(() => {
		let et = listContext.entityTypes.filter((item) => { return item.name.toLowerCase() == "customer" })

		if (et.length > 0) {
			setEntityTypeId(et[0].value);
		}

	}, [id])

	const getCustomer = async () => {
		if (id) {
			CustomerService.getCustomer(id)
				.then((response) => {
					console.log('CustomerService.getCustomer: SUCCESS', response);
					setCustomer(CustomerService.mapCustomer(response.data));
				})
				.catch((error) => {
					console.log('CustomerService.getCustomer: ERROR', error);
				})
				.finally(() => {
					setIsLoadingCustomer(false);
					setIsInitialLoad(false);
				});
		}
	}

	const loadAppSettings = async () => {
		const brandsSetting = (await AppSettingService.getAppSettingByName("BrandsRequiringApiKeys"))?.data;
		setBrandsWithApiKeys(AppSettingService.mapAppSetting(brandsSetting).valueAsString().split(",").filter(b => b?.length > 0).map(b => b.trim()));
	}

	const handleDocumentDeleted = (id: number) => {
		customer.documents = customer.documents.filter(doc => doc.id !== id);
		setCustomer({ ...customer });
	}

	const handleOperationsUpdated = (operationsUpdated: ICustomerOperations) => {
		setCustomer({ ...customer, operations: operationsUpdated });
	}


	return (
		<div className="hwi-customer-edit">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageCustomers}>
					<>
						<Spinner dependency={isInitialLoad && (isLoadingCustomer || brandswithApiKeys === undefined)} text="Loading customer...">
							<section className="management-page-header">
								<div className="title">
									<NavLink to="/customer">
										<Icons.GoBack fill="#9399ad" height="24px"></Icons.GoBack>
									</NavLink>
									{customer?.details?.name}
								</div>
								<div className="tabs">
									<div className={activeTab == "details" ? "tab active" : "tab"} onClick={() => setActiveTab("details")}>Details</div>
									<div className={activeTab == "addresses" ? "tab active" : "tab"} onClick={() => setActiveTab("addresses")}>Addresses</div>
									<div className={activeTab == "contact" ? "tab active" : "tab"} onClick={() => setActiveTab("contact")}>Contact</div>
									<div className={activeTab == "categories" ? "tab active" : "tab"} onClick={() => setActiveTab("categories")}>Codes</div>
									<div className={activeTab == "locations" ? "tab active" : "tab"} onClick={() => setActiveTab("locations")}>Locations</div>
									<div className={activeTab == "operations" ? "tab active" : "tab"} onClick={() => setActiveTab("operations")}>Operations</div>
									<div className={activeTab == "documents" ? "tab active" : "tab"} onClick={() => setActiveTab("documents")}>Documents</div>
								</div>
							</section>
							<Spinner dependency={isLoadingCustomer} text="Loading customer...">
								{(activeTab == "details" && customer?.details) &&
									<CustomerDetailsForm action="edit" id={id!} details={customer?.details!} brandsRequiringApiKeys={brandswithApiKeys} reloadData={getCustomer}></CustomerDetailsForm>
								}
								{activeTab == "addresses" &&
									<CustomerAddressesForm id={id!} shippingAddress={customer?.shippingAddress} mailingAddress={customer!.mailingAddress} reloadData={getCustomer}></CustomerAddressesForm>
								}
								{activeTab == "contact" &&
									<CustomerContactForm id={id!} contact={customer?.contact}></CustomerContactForm>
								}
								{activeTab == "categories" &&
									<CustomerCategoriesForm customer={customer} refreshCallback={getCustomer}></CustomerCategoriesForm>
								}
								{activeTab == "locations" &&
									<CustomerLocationsForm customer={customer} refreshCallback={getCustomer}></CustomerLocationsForm>
								}
								{activeTab == "operations" &&
									<CustomerOperationsForm id={id!} operations={customer?.operations} onOperationsUpdated={handleOperationsUpdated}></CustomerOperationsForm>
								}
								{activeTab == "documents" &&
									<PermissionGate permission={userContext.permissions?.canManageCustomerDocuments}>
										<div className="hwi-document-list">
											<DocumentTable
												data={customer?.documents}
												entityId={id}
												entityTypeId={entityTypeId}
												hideAddDocumentsLink={false}
												hideDeleteDocumentsLink={false}
												onRequestListRefresh={getCustomer}
												onViewDocument={(id) => { setSelectedDocumentId(id?.toString()) }}
												onDocumentDeleted={(id) => { handleDocumentDeleted(id) }}
											></DocumentTable>
											{selectedDocumentId &&
												<DocumentViewer documentId={+selectedDocumentId}></DocumentViewer>
											}

										</div>
									</PermissionGate>
								}
							</Spinner>
						</Spinner>
					</>
				</PermissionGate>
				{/* <Json data={customer}></Json> */}
			</Container>
		</div>
	);
};

export default CustomerEdit;


